import { Graph, Txn } from "./graph";
import type { GraphEdge } from "../types";

/** Perpendicular distance of p to segment a-b (point distance if a == b). */
function perpDist(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  return Math.hypot(ax + t * dx - px, ay + t * dy - py);
}

function douglasPeucker(pts: { x: number; y: number }[], tol: number): boolean[] {
  const keep = new Array(pts.length).fill(false);
  keep[0] = keep[pts.length - 1] = true;
  const stack: [number, number][] = [[0, pts.length - 1]];
  while (stack.length) {
    const [first, last] = stack.pop()!;
    let maxD = tol;
    let idx = -1;
    for (let i = first + 1; i < last; i++) {
      const d = perpDist(pts[i].x, pts[i].y, pts[first].x, pts[first].y, pts[last].x, pts[last].y);
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (idx !== -1) {
      keep[idx] = true;
      stack.push([first, idx], [idx, last]);
    }
  }
  return keep;
}

interface Chain {
  nodes: number[]; // node ids along the chain, anchors at both ends
  edges: GraphEdge[];
  classId: number;
  cycle: boolean;
}

/** An anchor must survive simplification: junction, line end, or class boundary. */
function isAnchor(g: Graph, i: number): boolean {
  const es = g.edgesOf(i);
  if (es.length !== 2) return true;
  return es[0].c !== es[1].c;
}

function collectChains(g: Graph): Chain[] {
  const visited = new Set<number>();
  const chains: Chain[] = [];

  const walk = (start: number, firstEdge: GraphEdge): Chain => {
    const nodes = [start];
    const edges: GraphEdge[] = [];
    let cur = start;
    let edge = firstEdge;
    for (;;) {
      edges.push(edge);
      visited.add(edge.i);
      const nxt = edge.a === cur ? edge.b : edge.a;
      nodes.push(nxt);
      if (isAnchor(g, nxt)) break;
      const es = g.edgesOf(nxt);
      const nextEdge = es[0].i === edge.i ? es[1] : es[0];
      if (nextEdge.c !== edge.c || visited.has(nextEdge.i)) break;
      cur = nxt;
      edge = nextEdge;
    }
    return { nodes, edges, classId: firstEdge.c, cycle: nodes[0] === nodes[nodes.length - 1] };
  };

  for (const i of g.nodes.keys()) {
    if (!isAnchor(g, i)) continue;
    for (const e of g.edgesOf(i)) {
      if (!visited.has(e.i)) chains.push(walk(i, e));
    }
  }
  // remaining edges are pure same-class cycles with no anchor; start anywhere
  for (const e of g.edges.values()) {
    if (!visited.has(e.i)) chains.push(walk(e.a, e));
  }
  return chains;
}

/** Simplifies all chains; returns the number of removed nodes. One undo step. */
export function simplifyGraph(g: Graph, tolerance: number): number {
  const chains = collectChains(g);
  const txn = new Txn(g);
  let removed = 0;
  for (const chain of chains) {
    if (chain.nodes.length < 3) continue;
    const pts = chain.nodes.map((i) => {
      const n = g.nodes.get(i)!;
      return { x: n.x, y: n.y };
    });
    const keep = douglasPeucker(pts, tolerance);
    if (chain.cycle && keep.filter(Boolean).length < 4) continue; // don't collapse small loops
    const removedHere = keep.filter((k) => !k).length;
    if (removedHere === 0) continue;
    for (const e of chain.edges) txn.delEdge(e.i);
    const keptNodes: number[] = [];
    chain.nodes.forEach((nid, idx) => {
      if (keep[idx]) {
        keptNodes.push(nid);
      } else {
        const n = g.nodes.get(nid)!;
        txn.do({ t: "delNode", n: { ...n } });
      }
    });
    for (let k = 0; k + 1 < keptNodes.length; k++) {
      txn.addEdge(keptNodes[k], keptNodes[k + 1], chain.classId);
    }
    removed += removedHere;
  }
  txn.commit();
  return removed;
}
